/**
 * My learning — everything assigned to or started by the caller, in the three
 * groups the rail's count is built from.
 *
 * Finished work stays on the page rather than disappearing: an organisation's
 * training record is the reason most of these courses exist, and a learner who
 * cannot find what they completed has to ask somebody.
 */

import { useEffect } from 'react';
import type { AssignedCourseWire } from '@wire';
import { learn } from '../api';
import { AssignedRow } from '../components/Bits';
import { CertificateList } from '../components/Certificates';
import { Empty, ErrorNote, Loading, PageTitle, Panel, PanelHead } from '../components/Chrome';
import { setAssignedCount } from '../counts';
import { ds } from '../ds';
import { useRouter } from '../router';
import { meta, plural } from '../theme';
import { useLoad } from '../useLoad';

function Group({ title, items }: { title: string; items: AssignedCourseWire[] }) {
  if (items.length === 0) return null;
  return (
    <Panel style={{ display: 'grid', gap: 4 }}>
      <PanelHead title={title} meta={plural(items.length, 'course')} />
      {items.map((item) => (
        <AssignedRow key={item.course.id} item={item} />
      ))}
    </Panel>
  );
}

export default function Learning() {
  const { Button } = ds();
  const { navigate } = useRouter();
  const { data, error, loading } = useLoad(() => learn.learning(), 'Could not load your courses.');
  const certificates = useLoad(() => learn.certificates(), 'Could not load your certificates.');

  // The rail's count, published rather than fetched again — see counts.ts.
  useEffect(() => {
    if (data) setAssignedCount(data.counts.assigned);
  }, [data]);

  if (error) return <ErrorNote>{error}</ErrorNote>;
  if (!data) return loading ? <Loading /> : null;

  const empty =
    data.inProgress.length === 0 && data.notStarted.length === 0 && data.finished.length === 0;

  return (
    <>
      <PageTitle
        title="My learning."
        meta={meta(
          `${data.counts.assigned} assigned`,
          data.counts.overdue > 0 ? `${data.counts.overdue} overdue` : null,
          `${data.counts.finishedThisMonth} finished this month`
        )}
      />

      {empty ? (
        <Empty line="Nothing is assigned to you.">
          <Button variant="secondary" size="sm" onClick={() => navigate('/catalogue')}>
            Browse catalogue
          </Button>
        </Empty>
      ) : (
        <>
          <Group title="In progress" items={data.inProgress} />
          <Group title="Not started" items={data.notStarted} />
          <Group title="Finished" items={data.finished} />
        </>
      )}

      {certificates.data && certificates.data.certificates.length > 0 && (
        <Panel style={{ display: 'grid', gap: 4 }}>
          <PanelHead
            title="Certificates"
            meta={plural(certificates.data.certificates.length, 'certificate')}
          />
          <CertificateList certificates={certificates.data.certificates} showLearner={false} />
        </Panel>
      )}
    </>
  );
}
